import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface TripwireProps {
  apiUrl: string;
}

type DecoyKind = 'mcp_server' | 'mcp_tool' | 'api_endpoint';

interface DecoyAsset {
  kind: DecoyKind;
  name: string;
  target: string;
  description: string;
}

interface AssetCounts {
  total: number;
  mcp_server: number;
  mcp_tool: number;
  api_endpoint: number;
}

interface TripEvent {
  id: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  result: string;
  agent_id: string;
  org_id: string;
  arguments?: string;
  client_ip?: string;
  user_agent?: string;
}

interface TripStats {
  total: number;
  trips_today: number;
  unique_attackers: number;
}

const KIND_LABEL: Record<DecoyKind, string> = {
  mcp_server: 'pages.tripwire.kindMcpServer',
  mcp_tool: 'pages.tripwire.kindTool',
  api_endpoint: 'pages.tripwire.kindApi',
};

const KIND_ICON: Record<DecoyKind, string> = {
  mcp_server: 'server',
  mcp_tool: 'bolt',
  api_endpoint: 'signal',
};

const Tripwire: React.FC<TripwireProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState<DecoyAsset[]>([]);
  const [counts, setCounts] = useState<AssetCounts | null>(null);
  const [events, setEvents] = useState<TripEvent[]>([]);
  const [stats, setStats] = useState<TripStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, e] = await Promise.all([
        fetch(`${apiUrl}/admin/tripwire/assets`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/tripwire/events?limit=200`).then((r) => r.json()),
      ]);
      setAssets(a.assets || []);
      setCounts(a.counts || null);
      setEvents(e.events || []);
      setStats(e.stats || null);
    } catch (err: any) {
      setError(`Cannot reach tripwire API: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 10000); // live trip feed
    return () => clearInterval(id);
  }, [loadAll]);

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="warning" size={20} /> {t('pages.tripwire.tripwire')}
          </h1>
          <p className="subtitle">
            {t('pages.tripwire.decoyServersToolsAndAPIsThat')}
          </p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <button className="ent-btn" onClick={loadAll} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} /> {t('pages.tripwire.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="ent-empty">{error}</div>}

      {/* Headline tiles */}
      <div className="metric-row">
        <div className="metric accent-info">
          <span className="label">
            <Icon name="shield" /> {t('pages.tripwire.decoysArmed')}
          </span>
          <div className="value">{counts?.total ?? '—'}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="warning" /> {t('pages.tripwire.tripsToday')}
          </span>
          <div className="value">{stats?.trips_today ?? '—'}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="users" /> {t('pages.tripwire.uniqueAttackers')}
          </span>
          <div className="value">{stats?.unique_attackers ?? '—'}</div>
        </div>
        <div className="metric accent-info">
          <span className="label">
            <Icon name="server" /> {t('pages.tripwire.serversToolsAPIs')}
          </span>
          <div className="value">
            {counts ? `${counts.mcp_server} / ${counts.mcp_tool} / ${counts.api_endpoint}` : '—'}
          </div>
        </div>
      </div>

      {/* Armed decoy assets */}
      <h2 style={{ fontSize: 14, margin: '20px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="shield" size={16} /> {t('pages.tripwire.armedDecoys', { count: assets.length })}
      </h2>
      <div className="ent-card">
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>{t('pages.tripwire.type')}</th>
                <th>{t('pages.tripwire.name')}</th>
                <th style={{ width: 220 }}>{t('pages.tripwire.attachedTo')}</th>
                <th>{t('pages.tripwire.bait')}</th>
                <th style={{ width: 90 }}>{t('pages.tripwire.status')}</th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 && (
                <tr>
                  <td colSpan={5} className="ent-empty">{t('pages.tripwire.noDecoysArmed')}</td>
                </tr>
              )}
              {assets.map((a, i) => (
                <tr key={`${a.kind}-${a.name}-${i}`}>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name={KIND_ICON[a.kind] as any} size={14} /> {t(KIND_LABEL[a.kind])}
                    </span>
                  </td>
                  <td><code style={{ fontFamily: 'monospace' }}>{a.name}</code></td>
                  <td><code style={{ fontFamily: 'monospace', fontSize: 11 }}>{a.target}</code></td>
                  <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{a.description}</td>
                  <td><span className="badge badge-success">{t('pages.tripwire.armed')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trip feed */}
      <h2 style={{ fontSize: 14, margin: '20px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="warning" size={16} /> {t('pages.tripwire.tripFeed', { count: events.length })}
      </h2>
      <div className="ent-card">
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th style={{ width: 170 }}>{t('pages.tripwire.time')}</th>
                <th style={{ width: 160 }}>{t('pages.tripwire.decoy')}</th>
                <th style={{ width: 160 }}>{t('pages.tripwire.attacker')}</th>
                <th style={{ width: 130 }}>{t('pages.tripwire.sourceIP')}</th>
                <th>{t('pages.tripwire.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={5} className="ent-empty">
                    {t('pages.tripwire.noTripsYetWhenAnAttacker')}
                  </td>
                </tr>
              )}
              {events.map((e) => (
                <tr key={e.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {parseServerDate(e.timestamp).toLocaleString()}
                  </td>
                  <td><code style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.tool_name}</code></td>
                  <td style={{ fontSize: 12 }}>{e.agent_id}</td>
                  <td style={{ fontSize: 12 }}>{e.client_ip || '—'}</td>
                  <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{e.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Tripwire;
